/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import {
    AfterViewInit,
    Component,
    ContentChild,
    ElementRef,
    EventEmitter,
    HostListener,
    Input,
    OnDestroy,
    Output,
    TemplateRef,
    ViewChild
} from '@angular/core';
import { BaseComponent } from '../../base.component';
import { LoadingService, LogService, ModalService } from '../../../services';
import { ModalBodyComponent } from './modal-body.component';
import { ModalFooterComponent } from './modal-footer.component';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-modal-wrapper',
    templateUrl: './modal-wrapper.component.html',
    styleUrls: ['./modal-wrapper.component.scss']
})
export class ModalWrapperComponent extends BaseComponent implements AfterViewInit, OnDestroy {
    /**
     * Check if the wrapper contains a modal body component.
     */
    @ContentChild(ModalBodyComponent, { descendants: true })
    public parentModalBodyComponent: ModalBodyComponent;

    /**
     * Check if the wrapper contains a modal footer component.
     * */
    @ContentChild(ModalFooterComponent, { descendants: true })
    public parentModalFooterComponent: ModalFooterComponent;

    /**
     * Reference to modal content to pass to the parent modal component.
     */
    @ViewChild('contentWrapper')
    public templateRef: TemplateRef<any>;

    /**
     * Reference to modal body wrapper.
     */
    @ViewChild('modalBodyWrapper')
    public modalBodyWrapper: ElementRef;

    /**
     * Is modal draggable.
     */
    @Input() public isDraggable: boolean = true;

    /**
     * Is modal overlay blurred.
     */
    @Input() public isOverlayBlurred: boolean = false;

    /**
     * Is the overlay clickable - if true the clicking the overlay will close the modal.
     */
    @Input() public isOverlayClickable: boolean = true;

    /**
     * Modal ID reference.
     */
    @Input() public modalId: string;

    /**
     * Modal height.
     */
    @Input() public modalHeight: number;

    /**
     * Modal width.
     */
    @Input() public modalWidth: number = 400;

    /**
     * Modal title must be passed a translation key.
     */
    @Input() public modalTitle: string;

    /**
     * Toggle displaying the close button top right.
     */
    @Input() public showCloseButton: boolean = true;

    /**
     * Flex styling alignment for the body.
     */
    @Input() public bodyAlignment: 'content-space-evenly' | 'content-left' | 'content-center' | 'content-right' = 'content-right';

    /**
     * Enables you to remove body padding for the wizard or other large content.
     */
    @Input() public removeBodyPadding: boolean = false;

    /**
     * Sets if footer should show.
     */
    @Input() public showFooter: boolean = false;

    /**
     * Flex styling alignment for footer buttons.
     */
    @Input() public footerButtonAlignment: 'content-space-evenly' | 'content-left' | 'content-center' | 'content-right' = 'content-left';

    /**
     * Toggle displaying the cancel button.
     */
    @Input() public showCancelButton: boolean = true;

    /**
     * Text to display on the submit button.
     */
    @Input() public submitButtonTextKey: string = 'done';

    /**
     * Option to enable/disable text translation.
     */
    @Input() public translateText: boolean = true;

    /**
     * Submit button emitter.
     */
    @Output() public submitButton: EventEmitter<any> = new EventEmitter();

    /**
     * Cancel button emitter.
     */
    @Output() public cancelSubmission: EventEmitter<any> = new EventEmitter();

    /**
     * Close emitter
     */
    @Output() public closed: EventEmitter<any> = new EventEmitter<any>();

    /**
     * Height after calculations.
     */
    public adjustedHeight: number;

    /**
     * Width after calculations.
     */
    public adjustedWidth: number;

    /**
     * Max height set to be window-inner height.
     */
    public maxHeight: number;

    /**
     * Global set based on content of the footer children
     */
    public hasFooterComponent: boolean = false;

    /**
     * Store the drag state of the modal.
     */
    public dragActive: boolean = false;

    /**
     * Primary button clicked state.
     */
    public primaryButtonClicked: boolean = false;

    /**
     * Store the footer primary button.
     * @private
     */
    private primaryButton: HTMLButtonElement;

    /**
     * Store the footer cancel button.
     * @private
     */
    private cancelButton: HTMLButtonElement;

    constructor(
        private loadingService: LoadingService,
        private logService: LogService,
        private modalService: ModalService
    ) {
        super();
    }

    ngAfterViewInit(): void {
        this.hasFooterComponent = !!this.parentModalFooterComponent;

        this.registerModalWithService();
        this.setInitialModalSize();
        this.updateModalSizeBasedOnClientSize();
        this.subscribeToDragActive();
        this.listenForFooterClicks();
        this.subscribeToLoadingButton();
        this.subscribeToModalChanges();
    }

    ngOnDestroy(): void {
        this.primaryButton?.removeEventListener('click', () => this.clickFunction());
        this.destroyed$.next(true);
        this.destroyed$.complete();
    }

    /**
     * Close parent modal on Enter key press.
     * @param $event
     */
    public handleKeyDown($event: KeyboardEvent): void {
        if ($event.code === 'Enter') {
            this.modalService.closeParentModalEmitter.next(this.modalId);
        }
    }

    /**
     * Close modal by ID.
     */
    public close(): void {
        if (!this.primaryButtonClicked) {
            this.modalService.closeParentModalEmitter.next(this.modalId);
        }
    }

    /**
     * Emits when cancel is clicked.
     */
    public onCancel(): void {
        this.cancelSubmission.emit();
        this.close();
    }

    /**
     * Emits when submit is clicked.
     */
    public onSubmit(): void {
        this.primaryButtonClicked = false;
        this.submitButton.emit();
    }

    /**
     * Register modal with service.
     * @private
     */
    private registerModalWithService() {
        this.modalService.modals = {
            template: this.templateRef,
            id: this.modalId,
            options: {
                isDraggable: this.isDraggable,
                isOverlayBlurred: this.isOverlayBlurred,
                isOverlayClickable: this.isOverlayClickable
            }
        };
    }

    /**
     * Set initial modal size from inputs.
     * @private
     */
    private setInitialModalSize() {
        this.adjustedHeight = this.modalHeight;
        this.adjustedWidth = this.modalWidth;
    }

    /**
     * Update the modal size based on client size.
     * @private
     */
    @HostListener('window:resize', ['$event'])
    private updateModalSizeBasedOnClientSize(): void {
        // There is no default height, so set the max height as the window innerHeight - 50px
        if (!this.modalHeight) {
            this.maxHeight = window.innerHeight - 50;
        }

        if (this.modalHeight > window.innerHeight) {
            this.adjustedHeight = window.innerHeight - 50;
            this.logService.debug(
                'ParentModalWrapperComponent: updateModalSizeBasedOnClientSize',
                'Modal height has been reduced to fit the screen size'
            );
        }

        if (this.modalWidth > window.innerWidth) {
            this.adjustedWidth = window.innerWidth - 50;
            this.logService.debug(
                'ParentModalWrapperComponent: updateModalSizeBasedOnClientSize',
                'Modal width has been reduced to fit the screen size'
            );
        }
    }

    /**
     * Subscribe to drag active.
     * @private
     */
    private subscribeToDragActive(): void {
        this.modalService.dragActiveEmitter
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (state: boolean): void => {
                    this.dragActive = state;
                },
                error: (error: any): void => {
                    this.logService.error(
                        'ModalWrapperComponent: subscribeToDragActive',
                        'Subscribing to drag active failed ' + error
                    );
                }
            });
    }

    /**
     * Listen for clicks in the footer component.
     * @private
     */
    private listenForFooterClicks(): void {
        const footerContent = this.parentModalFooterComponent?.footer?.nativeElement;
        if (footerContent) {
            this.primaryButton = footerContent.querySelector('button.primary.loading-button');
            this.cancelButton = footerContent.querySelector('button.outline');
            if (this.primaryButton) {
                this.primaryButton.addEventListener('click', () => this.clickFunction());
            }
        }
    }

    /**
     * Click helper function for clean up.
     * @private
     */
    private clickFunction(): void {
        this.primaryButtonClicked = true;
        this.cancelButton.disabled = true;
    }

    /**
     * Subscribe to loading button service to clear disabled state.
     * @private
     */
    private subscribeToLoadingButton(): void {
        this.loadingService.loadingButtonEmitter
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (response: any): void => {
                    if (typeof (response) === 'boolean') {
                        if (!response) {
                            this.resetButtonStates();
                        }
                    } else {
                        if (!response.state) {
                            this.resetButtonStates();
                        }
                    }
                }
            });
    }

    /**
     * Helper function to reset button states.
     * @private
     */
    private resetButtonStates(): void {
        this.primaryButtonClicked = false;
        this.cancelButton.disabled = false;
        this.primaryButton?.removeEventListener('click', () => this.clickFunction());
    }

    /**
     * Make sure button state is correct when the modal opens.
     * @private
     */
    private subscribeToModalChanges(): void {
        this.modalService.openParentModalEmitter
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (modal: any): void => {
                    if (modal.id === this.modalId) {
                        const timerId = setTimeout((): void => {
                            this.primaryButtonClicked = false;
                            this.cancelButton.disabled = false;
                            clearTimeout(timerId);
                        }, 250);
                    }
                }
            });
    }
}
